// material
import { Box, Divider, Link, Stack, Typography, useMediaQuery } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useTheme } from '@emotion/react';
// amenity icons
import AllInclusiveIcon from '@mui/icons-material/AllInclusive';
import AtmIcon from '@mui/icons-material/Atm';
import CarRepairIcon from '@mui/icons-material/CarRepair';
import FaceRetouchingNaturalIcon from '@mui/icons-material/FaceRetouchingNatural';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import HotelIcon from '@mui/icons-material/Hotel';
import LocalBarIcon from '@mui/icons-material/LocalBar';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import LocalParkingIcon from '@mui/icons-material/LocalParking';
import LocalPharmacyIcon from '@mui/icons-material/LocalPharmacy';
import MedicationIcon from '@mui/icons-material/Medication';
import ParkIcon from '@mui/icons-material/Park';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import StadiumIcon from '@mui/icons-material/Stadium';
import TempleHinduIcon from '@mui/icons-material/TempleHindu';
import TheatersIcon from '@mui/icons-material/Theaters';
import WcIcon from '@mui/icons-material/Wc';
// section / contact icons
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import StorefrontRoundedIcon from '@mui/icons-material/StorefrontRounded';
import SupportAgentRoundedIcon from '@mui/icons-material/SupportAgentRounded';
import CallRoundedIcon from '@mui/icons-material/CallRounded';
import EmailRoundedIcon from '@mui/icons-material/EmailRounded';
import { paymentStyles } from "./QRScanner/payment.constants";
// ----------------------------------------------------------------------

const DAYS = [
    { key: "sunday", label: "Sunday" },
    { key: "monday", label: "Monday" },
    { key: "tuesday", label: "Tuesday" },
    { key: "wednesday", label: "Wednesday" },
    { key: "thursday", label: "Thursday" },
    { key: "friday", label: "Friday" },
    { key: "saturday", label: "Saturday" },
];

const AMENITIES = [
    { key: "PARKING", label: "Parking", Icon: LocalParkingIcon },
    { key: "RESTAURANT", label: "Restaurant", Icon: RestaurantMenuIcon },
    { key: "SHOPPING", label: "Shopping", Icon: ShoppingCartIcon },
    { key: "ATM", label: "ATM", Icon: AtmIcon },
    { key: "HOTEL", label: "Hotel", Icon: HotelIcon },
    { key: "BAR", label: "Bar", Icon: LocalBarIcon },
    { key: "24/7 MONITORING", label: "24/7 Monitoring", Icon: AllInclusiveIcon },
    { key: "HOSPITAL", label: "Hospital", Icon: LocalHospitalIcon },
    { key: "PHARMACY", label: "Pharmacy", Icon: LocalPharmacyIcon },
    { key: "MEDICAL STORE", label: "Medical Store", Icon: MedicationIcon },
    { key: "AUTO SERVICE", label: "Auto Service", Icon: CarRepairIcon },
    { key: "KID ACTIVITY ZONE", label: "Kids Zone", Icon: StadiumIcon },
    { key: "PARK", label: "Park", Icon: ParkIcon },
    { key: "PUBLIC WASHROOM", label: "Washroom", Icon: WcIcon },
    { key: "PILGRIMAGE SITE", label: "Pilgrimage", Icon: TempleHinduIcon },
    { key: "SALON", label: "Salon", Icon: FaceRetouchingNaturalIcon },
    { key: "THEATER", label: "Cinema", Icon: TheatersIcon },
    { key: "GYM", label: "Gym", Icon: FitnessCenterIcon },
];

const cardSx = {
    width: "100%",
    borderRadius: "12px",
    border: "1px solid",
    borderColor: (t) => alpha(t.palette.grey[500], 0.2),
    backgroundColor: "#fff",
    p: 1.75,
};

const SectionHeader = ({ icon: IconCmp, children }) => (
    <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 1.25 }}>
        <IconCmp sx={{ fontSize: 18, color: "primary.main" }} />
        <Typography sx={{ fontWeight: 700, fontSize: "0.9rem", letterSpacing: 0.2, color: "text.primary" }}>
            {children}
        </Typography>
    </Stack>
);

const ContactRow = ({ icon: IconCmp, value, href }) => (
    <Stack direction="row" alignItems="center" spacing={1}>
        <IconCmp sx={{ fontSize: 18, color: "primary.main", flexShrink: 0 }} />
        {href ? (
            <Link
                href={href}
                underline="hover"
                sx={{ fontSize: 13.5, fontWeight: 600, color: "text.primary", wordBreak: "break-word" }}
            >
                {value}
            </Link>
        ) : (
            <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: "text.disabled" }}>{value}</Typography>
        )}
    </Stack>
);

export default function MarkerInformationTab({ locationDetail, forLocationTab }) {
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const todayIdx = new Date().getDay();

    const getOpenTime = (day) => {
        if (!locationDetail || !locationDetail.oeningHours || !locationDetail.oeningHours[day]) {
            return "N/A";
        }
        return locationDetail.oeningHours[day].allDay ? "24 x 7" : "N/A";
    };

    const availableAmenities = AMENITIES.filter((a) => locationDetail?.amenities?.includes(a.key));

    const phone = locationDetail?.phone || locationDetail?.alternatePhone || "N/A";
    const email = locationDetail?.email || "N/A";

    const amenityCols = isSmallScreen ? 4 : forLocationTab ? 4 : 5;

    const hoursByDay = DAYS.map((d, i) => ({ ...d, hours: getOpenTime(d.key), isToday: i === todayIdx }));
    const uniqueHours = [...new Set(hoursByDay.map((h) => h.hours))];
    const uniformHours = uniqueHours.length === 1 ? uniqueHours[0] : null;

    const sectionDivider = <Divider sx={{ my: 1.75 }} />;

    return (
        <Box sx={cardSx}>
            <SectionHeader icon={AccessTimeRoundedIcon}>Operating Hours</SectionHeader>
            {uniformHours ? (
                <Box
                    sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        px: 1.5,
                        py: 0.5,
                        borderRadius: "8px",
                        fontSize: 13,
                        fontWeight: 600,
                        bgcolor: (t) =>
                            alpha(uniformHours === "24 x 7" ? t.palette.success.main : t.palette.grey[500], 0.12),
                        color: uniformHours === "24 x 7" ? "success.dark" : "text.secondary",
                    }}
                >
                    {uniformHours === "24 x 7" ? "Open 24 × 7 · All days" : "Operating hours not available"}
                </Box>
            ) : (
                <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", columnGap: 2, rowGap: 0.25 }}>
                    {hoursByDay.map((h) => (
                        <Stack
                            key={h.key}
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                            sx={{
                                py: 0.35,
                                px: 0.75,
                                borderRadius: "6px",
                                ...(h.isToday && { bgcolor: (t) => alpha(t.palette.primary.main, 0.06) }),
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: 12.5,
                                    fontWeight: h.isToday ? 700 : 500,
                                    color: h.isToday ? "primary.main" : "text.primary",
                                }}
                            >
                                {h.label.slice(0, 3)}
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: 12,
                                    fontWeight: 600,
                                    color: h.hours !== "N/A" ? "success.main" : "text.disabled",
                                }}
                            >
                                {h.hours}
                            </Typography>
                        </Stack>
                    ))}
                </Box>
            )}

            {sectionDivider}

            <SectionHeader icon={StorefrontRoundedIcon}>Nearby Amenities</SectionHeader>
            {availableAmenities.length > 0 ? (
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: `repeat(${amenityCols}, 1fr)`,
                        columnGap: 1,
                        rowGap: 1.25,
                    }}
                >
                    {availableAmenities.map((a) => (
                        <Stack key={a.key} alignItems="center" spacing={0.5}>
                            <Box
                                sx={{
                                    width: 38,
                                    height: 38,
                                    borderRadius: "10px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    bgcolor: (t) => alpha(t.palette.primary.main, 0.1),
                                    color: "primary.main",
                                }}
                            >
                                <a.Icon sx={{ fontSize: 20 }} />
                            </Box>
                            <Typography
                                sx={{
                                    fontSize: 10.5,
                                    fontWeight: 500,
                                    textAlign: "center",
                                    lineHeight: 1.15,
                                    color: "text.secondary",
                                }}
                            >
                                {a.label}
                            </Typography>
                        </Stack>
                    ))}
                </Box>
            ) : (
                <Typography variant="body2" color="text.disabled">
                    No amenities listed nearby
                </Typography>
            )}

            {sectionDivider}

            <SectionHeader icon={SupportAgentRoundedIcon}>Operator</SectionHeader>
            <Stack spacing={0.75}>
                <ContactRow
                    icon={CallRoundedIcon}
                    value={phone}
                    href={phone !== "N/A" ? `tel:${phone}` : undefined}
                />
                <ContactRow
                    icon={EmailRoundedIcon}
                    value={email}
                    href={email !== "N/A" ? `mailto:${email}` : undefined}
                />
            </Stack>
        </Box>
    );
}
